import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Box, Button, Center, HStack, useTheme, VStack } from '@chakra-ui/react'
import { Formik } from 'formik'
import { useNavigate, useParams } from 'react-router-dom'
import { REPOSITORY } from '../../api/repository/Repository'
import InputForm from '../../components/InputForm/InputForm'
import UnderlineHeading from '../../components/UnderlineText/UnderlineHeading'
import { useLoadingContext } from '../../utils/hooks/Context/LoadingContext'
import { useRequestStatusContext } from '../../utils/hooks/Context/RequestStatusContext'
import InfoLabel from '../EmployeeDetails/components/InfoLabel'
import SelectorField from './../../components/Selector/SelectorField'
import { useRegions } from './NewBranchOffice'
import ConfirmModal from './subViews/ConfirmModal'
import IconButton from '../../components/IconButton/IconButton'
import { faBuilding } from '@fortawesome/free-solid-svg-icons'

export default function BranchOfficeDetails() {
  const [branchOfficeData, setBranchOfficeData] = useState(null)
  const [editable, setEditable] = useState(false)
  const { id } = useParams()
  const navigate = useNavigate()
  const {
    colors: { gray },
  } = useTheme()
  // const regions = REPOSITORY.local.company.getRegions()
  const { regions, foundedCommunes } = useRegions()
  const { sCentroCosto } = REPOSITORY.local.company.getCompanyData()
  const [openConfirm, setOpenConfirm] = useState(false)
  const { handleOpen: handleOpenLoading, handleClose: handleCloseLoading } =
    useLoadingContext()

  const { handleOpen, handleSetTitle, handleChangeBody, handleClose } =
    useRequestStatusContext()
  const Component = useMemo(() => {
    if (editable) return InputForm
    return InfoLabel
  })
  const SelectComponent = useMemo(() => {
    if (editable) return SelectorField
    return InfoLabel
  })

  const handleEdit = () => {
    setEditable((prevState) => !prevState)
  }
  const onBack = () => {
    navigate(-1)
  }
  const getData = useCallback(async () => {
    handleOpenLoading()
    await REPOSITORY.wsEmpresa.company
      .branchOfficeById(id)
      .then((response) => {
        setBranchOfficeData(response.data)
        handleCloseLoading()
      })
      .catch((error) => {
        console.error(error)
        setBranchOfficeData([])
        handleCloseLoading()
      })
  }, [])

  const onCloseDeleteResponse = useCallback(() => {
    navigate(-1)
    handleClose()
  }, [handleClose])

  const onOpenConfirm = () => {
    setOpenConfirm(true)
  }
  const onCloseConfirm = () => {
    setOpenConfirm(false)
  }

  const onOkStatus = useCallback(
    (response, title = null) => {
      handleCloseLoading()
      handleSetTitle(title ? title : 'Colaborador actualizado', response.status)
      handleChangeBody(
        <Button variant="primary" onClick={onCloseDeleteResponse}>
          {response.status === 200 ? 'Continuar' : 'Contactar a soporte ahora'}
        </Button>,
      )
      handleOpen()
    },
    [handleSetTitle, handleChangeBody, handleOpen, onCloseDeleteResponse],
  )

  const onError = useCallback(
    (response, title = null) => {
      handleCloseLoading()
      handleSetTitle(
        title ? title : 'Error al editar colaborador',
        response.status,
      )
      handleChangeBody(
        <Button variant="primary" onClick={handleClose}>
          {response.status === 200 ? 'Continuar' : 'Contactar a soporte ahora'}
        </Button>,
      )
      handleOpen()
    },
    [handleSetTitle, handleChangeBody, handleOpen, handleClose],
  )

  const onDelete = async () => {
    onCloseConfirm()
    handleOpenLoading()
    await REPOSITORY.wsEmpresa.company
      .deleteBranchOffice({ sSucursal: id })
      .then((res) => {
        onOkStatus(res, res.data.sMensaje)
      })
      .catch((error) => {
        onError(error.response, error.response.data.error)
      })
    handleCloseLoading()
  }

  useEffect(() => {
    getData()
    return () => {
      setBranchOfficeData([])
    }
  }, [])
  const selectedComune = (region, idComune) => {
    return foundedCommunes(region).find(
      (element) => element.idComuna === idComune,
    )
  }
  const noAssigneed = editable ? '' : 'Sin asignar'
  return (
    <VStack height="100vh">
      <ConfirmModal
        isOpen={openConfirm}
        text={
          'Está a punto de eliminar la sucursal seleccionada. ¿Desea continuar?'
        }
        onClose={onCloseConfirm}
        onConfirm={onDelete}
      />
      <Formik
        initialValues={{
          sCentroCosto: branchOfficeData?.sCentroCosto,
          sComuna: branchOfficeData?.sComuna,
          NombreSucursal: branchOfficeData?.NombreSucursal,
          sRegion: branchOfficeData?.sRegion,
          sDireccion: branchOfficeData?.sDireccion,
          NombreContacto: branchOfficeData?.InfoContacto?.[0]?.NombreContacto,
          TelefonoContacto:
            branchOfficeData?.InfoContacto?.[0]?.TelefonoContacto,
          EmailContacto: branchOfficeData?.InfoContacto?.[0]?.EmailContacto,
        }}
        enableReinitialize
        onReset={() => {
          return {
            sCentroCosto: branchOfficeData?.sCentroCosto,
            sComuna: branchOfficeData?.sComuna,
            NombreSucursal: branchOfficeData?.NombreSucursal,
            sRegion: branchOfficeData?.sRegion,
            sDireccion: branchOfficeData?.sDireccion,
            NombreContacto: branchOfficeData?.InfoContacto[0]?.NombreContacto,
            TelefonoContacto:
              branchOfficeData?.InfoContacto[0]?.TelefonoContacto,
            EmailContacto: branchOfficeData?.InfoContacto[0]?.EmailContacto,
          }
        }}
        onSubmit={async (values) => {
          handleOpenLoading()
          await REPOSITORY.wsEmpresa.company
            .updateBranchOffice({
              NombreSucursal: values.NombreSucursal,
              sSucursal: id,
              sDireccion: values.sDireccion,
              sComuna: values.sComuna,
              sRegion: values.sRegion,
              sCentroCosto: values.sCentroCosto,
              InfoContacto: [
                {
                  ...branchOfficeData?.InfoContacto?.[0],
                  NombreContacto: values.NombreContacto,
                  TelefonoContacto: values.TelefonoContacto,
                  EmailContacto: values.EmailContacto,
                },
              ],
            })
            .then((res) => {
              onOkStatus(res, res.data.sMensaje)
            })
            .catch((error) => {
              onError(error.response, error.response.data.error)
            })
        }}
      >
        {({ values, resetForm, handleChange, handleSubmit }) => (
          <VStack
            w="100%"
            h="75vh"
            alignItems="flex-end"
            justifyContent="space-between"
          >
            <VStack spacing="32px" w="100%" alignItems="flex-start">
              <UnderlineHeading
                text="Datos de Sucursal"
                variant="h5"
                color={gray['800']}
              />
              <HStack alignItems="flex-start" w="70%" spacing="16px">
                <VStack w="100%" spacing="16px">
                  <Component
                    id="NombreSucursal"
                    name="NombreSucursal"
                    onChange={handleChange}
                    stackProps={{ w: '100%' }}
                    title={'Nombre sucursal'}
                    value={values?.NombreSucursal}
                  />
                  <SelectComponent
                    stackProps={{ w: '100%' }}
                    title={'Región'}
                    name="sRegion"
                    id="sRegion"
                    value={
                      editable
                        ? values?.sRegion
                        : regions.find((item) => item.id === values?.sRegion)
                            ?.NombreRegion
                    }
                    options={regions}
                    titleType="overline"
                  />
                  <Component
                    id="sDireccion"
                    name="sDireccion"
                    onChange={handleChange}
                    stackProps={{ w: '100%' }}
                    title={'Dirección'}
                    value={values?.sDireccion}
                  />
                  <SelectComponent
                    stackProps={{ w: '100%' }}
                    title={'Comuna'}
                    name="sComuna"
                    id="sComuna"
                    value={
                      editable
                        ? selectedComune(values?.sRegion, values?.sComuna)?.id
                        : selectedComune(values?.sRegion, values?.sComuna)
                            ?.NombreComuna
                    }
                    options={foundedCommunes(values.sRegion)}
                    titleType="overline"
                  />
                  <SelectComponent
                    stackProps={{ w: '100%' }}
                    title={'Centro de costo'}
                    name="sCentroCosto"
                    id="sCentroCosto"
                    value={
                      editable
                        ? values?.sCentroCosto
                        : branchOfficeData?.CentroCosto || 'Sin asignar'
                    }
                    options={sCentroCosto}
                    titleType="overline"
                  />
                </VStack>
                <VStack w="100%" spacing="16px">
                  <Component
                    id="NombreContacto"
                    name="NombreContacto"
                    onChange={handleChange}
                    stackProps={{ w: '100%' }}
                    title={'Nombre contacto'}
                    value={
                      values?.NombreContacto || values?.NombreContacto === ''
                        ? values?.NombreContacto
                        : noAssigneed
                    }
                  />
                  <Component
                    id="TelefonoContacto"
                    name="TelefonoContacto"
                    onChange={handleChange}
                    stackProps={{ w: '100%' }}
                    title={'Teléfono de contacto'}
                    value={
                      values?.TelefonoContacto ||
                      values?.TelefonoContacto === ''
                        ? values?.TelefonoContacto
                        : noAssigneed
                    }
                  />
                  <Component
                    id="EmailContacto"
                    name="EmailContacto"
                    onChange={handleChange}
                    stackProps={{ w: '100%' }}
                    title={'Correo electrónico de contacto'}
                    value={
                      values?.EmailContacto || values?.EmailContacto === ''
                        ? values?.EmailContacto
                        : noAssigneed
                    }
                  />
                </VStack>
              </HStack>
              <UnderlineHeading
                text={'Acciones'}
                variant="h5"
                color={gray['800']}
              />
              <Box w="339px" h="56px" borderRadius={'6px'}>
                <IconButton
                  borderRadius={'6px'}
                  text={'ELIMINAR SUCURSAL'}
                  icon={faBuilding}
                  onClick={onOpenConfirm}
                  variant="error"
                  color="white"
                />
              </Box>
            </VStack>
            <Center w="100%">
              <HStack>
                <Button
                  w="100px"
                  h="40px"
                  onClick={
                    editable
                      ? () => {
                          resetForm()
                          handleEdit()
                        }
                      : onBack
                  }
                  variant="white"
                >
                  {editable ? 'Cancelar' : 'Volver'}
                </Button>
                <Button
                  w="100px"
                  h="40px"
                  onClick={editable ? handleSubmit : handleEdit}
                  variant="primary"
                >
                  {editable ? 'Guardar' : 'Editar'}
                </Button>
              </HStack>
            </Center>
          </VStack>
        )}
      </Formik>
    </VStack>
  )
}
